import {
    Alert,
    Box,
    Button,
    Snackbar,
    Step,
    StepLabel,
    Stepper,
} from "@mui/material";
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import bgLogin from "../../assets/images/bgLogin.png";
import { useState, useEffect, ReactNode, FormEvent } from "react";
import { useTranslation } from 'react-i18next';
import { routes } from "../../routes";
import usePasswordReset from "../../hooks/usePasswordReset";
import { useNavigate } from "react-router-dom";
import SummaryPage from "./summaryPage";
import CodeReset from "../../components/forgotPassword/CodeReset";
import EmailReset from "../../components/forgotPassword/EmailReset";
import PasswordReset from "../../components/forgotPassword/PasswordReset";
import LoginTemplate from "../../components/forgotPassword/LoginTemplate";

const AUTO_HIDE_DURATION = 6000;
type Action = () => Promise<void>
const REDIRECT_AFTER_N_SECOND = 10;
const ForgotPasswordPage = () => {
    const { email, setEmail, password, setPassword, confirmPassword, setConfirmPassword, onEmailSubmit, onCodeSubmit, onPasswordSubmit, code, setCode, isAlert, setIsAlert, alertMessage } = usePasswordReset();
    const { t } = useTranslation();
    const [activeStep, setActiveStep] = useState(0);
    const [count, setCount] = useState(REDIRECT_AFTER_N_SECOND);
    const navigate = useNavigate();
    const steps = [t("passwordReset.enterEmail"), t("passwordReset.enterCode"), t("passwordReset.enterPassword")];
    const stepActions = new Map<number, Action>([[0, onEmailSubmit], [1, onCodeSubmit], [2, onPasswordSubmit]]);
    const [startCountdown, setStartCountdown] = useState(false);
    const [isValidEmailForm, setIsValidEmailForm] = useState(true);
    const [isValidCodeForm, setIsValidCodeForm] = useState(true);
    const [isValidPasswordForm, setIsValidPasswordForm] = useState(false);

    useEffect(() => {
        if (activeStep === stepActions.size && !startCountdown) setStartCountdown(true)

        if (activeStep !== stepActions.size) {
            setStartCountdown(false);
            setCount(REDIRECT_AFTER_N_SECOND);
        }
        if (count === 0 && activeStep === stepActions.size) navigate(routes.login)
        if (!startCountdown) return;

        const interval = setInterval(() => {
            setCount((currentCount) => currentCount - 1);
        }, 1000);

        return () => clearInterval(interval);

    }, [activeStep, count, startCountdown]);


    const stepComponentsMap = new Map<number, ReactNode>([
        [0, <EmailReset setEmail={setEmail} email={email} setIsEmailFormValid={setIsValidEmailForm} />],
        [1, <CodeReset email={email} code={code} setCode={setCode} onEmailSubmit={onEmailSubmit} setIsValidCodeForm={setIsValidCodeForm} />],
        [2, <PasswordReset password={password} setPassword={setPassword} confirmPassword={confirmPassword} setConfirmPassword={setConfirmPassword} setIsValidPasswordForm={setIsValidPasswordForm} />],
    ]);

    const onNext = () => {
        const stepAction = stepActions.get(activeStep);
        stepAction && stepAction()
            .then(() => setActiveStep(activeStep + 1))
            .catch(() => setIsAlert(true));
    };
    const onBack = () => {
        if (activeStep === steps.length - 1) {
            setPassword("");
            setConfirmPassword("");
            setIsValidPasswordForm(false);
        }

        setActiveStep(activeStep - 1);
    };
    const onClose = () => setIsAlert(false);

    const isValidForm = () => {
        const formValidityMap = new Map([
            [0, isValidEmailForm],
            [1, isValidCodeForm],
            [2, isValidPasswordForm]
        ]);
        return formValidityMap.get(activeStep) || false;
    };

    const onSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (!isValidForm()) return;

        onNext();
    };
    return (
        <LoginTemplate bgLogin={bgLogin}  >
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: { sm: 'space-between', md: 'flex-end' },
                    alignItems: 'center',
                    width: '100%',
                    maxWidth: { sm: '100%', md: "80%" },
                }}
            >
                <Box
                    sx={{
                        display: { xs: 'flex', md: 'flex' },
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        alignItems: 'flex-end',
                        flexGrow: 1,
                        height: 100,
                        marginBottom: 5
                    }}
                >
                    <Button
                        startIcon={<ArrowBackRoundedIcon />}
                        href={routes.login}
                        sx={{ marginTop: 3, marginBottom: 2 }}
                    >
                        {t("passwordReset.back")}
                    </Button>

                    <Stepper
                        activeStep={activeStep}
                        alternativeLabel
                        sx={{
                            width: '100%',
                            height: 100,
                            display: { md: 'flex' },
                        }}                        >
                        {steps.map((label) => (
                            <Step
                                sx={{
                                    ':first-of-type': { pl: 0 },
                                    ':last-child': { pr: 0 },
                                    '& .MuiStepConnector-root': { top: { xs: 6, sm: 12 } },
                                }}
                                key={label}
                            >
                                <StepLabel sx={{ '.MuiStepLabel-labelContainer': { maxWidth: '70px' } }} >
                                    {label}
                                </StepLabel>
                            </Step>
                        ))}
                    </Stepper>

                </Box>
            </Box>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    flexGrow: 1,
                    width: '100%',
                    maxWidth: { sm: '100%', md: 600 },
                    marginTop: 1,
                    padding: 1
                }}
            >
                {activeStep === steps.length ? (
                    <SummaryPage count={count} />
                ) : (
                    <Box component="form" onSubmit={onSubmit} sx={{ display: 'flex', flexDirection: 'column' }}>
                        {stepComponentsMap.get(activeStep)}
                        {isAlert && <Snackbar open={isAlert} onClose={onClose} autoHideDuration={AUTO_HIDE_DURATION} anchorOrigin={{ vertical: 'top', horizontal: 'center' }} sx={{ position: 'absolute', width: '100%' }}>
                            <Alert severity='error'>{alertMessage}</Alert>
                        </Snackbar>}
                        <Box display="flex" justifyContent="space-between" padding={1} >
                            <Button
                                startIcon={<ChevronLeftRoundedIcon />}
                                onClick={onBack}
                                variant="outlined" sx={{ visibility: activeStep === 0 ? "hidden" : "block" }}>
                                {t("passwordReset.previous")}
                            </Button>
                            <Button
                                variant="contained"
                                endIcon={<ChevronRightRoundedIcon />}
                                type="submit"
                                disabled={!isValidForm()}
                                sx={{
                                    display: 'flex', alignContent: "flex-end", alignItems: "flex-end", textAlign: "end"
                                }}
                            >
                                {activeStep === steps.length - 1 ? t("passwordReset.confirm") : t("passwordReset.next")}
                            </Button>
                        </Box>
                    </Box>
                )}
            </Box>
        </LoginTemplate >
    );
};

export default ForgotPasswordPage;